import { Injectable } from '@angular/core';

export enum PortalType {
  PRIVATE = 'Private',
  BUSINESS = 'Business',
}

export interface MyPagesRouteParams {
  /** User id (private) or customer/business org id (business) */
  entityId: string;
  /** Parent/market organization id (VIKINGNO, VIKINGSE, VIKINGDK) */
  organizationId: string;
}

@Injectable({
  providedIn: 'root',
})
export class PortalContextService {
  private readonly PORTAL_CONTEXT_KEY = 'portal_context';
  private currentPortalType: PortalType | null = null;
  private myPagesRouteParams: MyPagesRouteParams | null = null;

  constructor() {
    // Load portal context from sessionStorage on initialization
    this.loadPortalContext();
  }

  /**
   * Set the current portal context (called during login)
   */
  setPortalContext(portalType: PortalType): void {
    this.currentPortalType = portalType;
    sessionStorage.setItem(this.PORTAL_CONTEXT_KEY, portalType);
  }

  /**
   * Get the current portal context
   */
  getPortalContext(): PortalType | null {
    return this.currentPortalType;
  }

  /**
   * Check if user is in the specified portal context
   */
  isInPortalContext(portalType: PortalType): boolean {
    return this.currentPortalType === portalType;
  }

  /**
   * Clear portal context (called during logout)
   */
  clearPortalContext(): void {
    this.currentPortalType = null;
    sessionStorage.removeItem(this.PORTAL_CONTEXT_KEY);
  }

  /**
   * Load portal context from sessionStorage
   */
  private loadPortalContext(): void {
    const storedContext = sessionStorage.getItem(this.PORTAL_CONTEXT_KEY);
    if (storedContext && Object.values(PortalType).includes(storedContext as PortalType)) {
      this.currentPortalType = storedContext as PortalType;
    }
  }

  /**
   * Check if portal context is set
   */
  hasPortalContext(): boolean {
    return this.currentPortalType !== null;
  }

  /**
   * My-pages route prefix for building links: /my-pages/user or /my-pages/business.
   * Use when building routerLink or navigate() for my-pages child routes.
   */
  getMyPagesRoutePrefix(): string {
    return this.currentPortalType === PortalType.BUSINESS ? '/my-pages/business' : '/my-pages/user';
  }

  /**
   * Set my-pages route params (entityId + organizationId). Called by PrivateComponent on load.
   * entityId = userId (private) or businessId (business); organizationId = parent org (VIKINGNO/SE/DK).
   */
  setMyPagesRouteParams(entityId: string, organizationId: string): void {
    this.myPagesRouteParams = { entityId, organizationId };
  }

  /**
   * Get my-pages route params for building links. Returns null if not set.
   * Use with getMyPagesRoutePrefix(): [prefix, params.entityId, params.organizationId, 'dashboard'].
   */
  getMyPagesRouteParams(): MyPagesRouteParams | null {
    return this.myPagesRouteParams;
  }

  /**
   * Build my-pages child route array for routerLink or navigate: [prefix, entityId, organizationId, path].
   * Returns empty array if route params not set.
   */
  getMyPagesLink(path: string): string[] {
    const params = this.myPagesRouteParams;
    if (!params) {
      return [];
    }
    return [this.getMyPagesRoutePrefix(), params.entityId, params.organizationId, path];
  }
}
